import { useMemo } from 'react';
import { Paper, Box } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import {
  formatIndianAuto,
  premiumTooltip,
  horizontalGrid,
  defaultAnimation,
  axisLabelStyle,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';
import { getCurrency as currencySelector } from 'utils/redux/reducer/authentication-slice';

const norm = s => String(s || '').toLowerCase();

const EFF_COLORS = {
  high: '#66BB6A',
  mid: '#FFC107',
  low: '#E57373',
};

const effColor = pct => {
  if (pct > 90) return EFF_COLORS.high;
  if (pct >= 60) return EFF_COLORS.mid;
  return EFF_COLORS.low;
};

function buildRows(data) {
  const labels = data?.labels || [];
  const datasets = data?.datasets || [];
  const budgetDs = datasets.find(d => norm(String(d.label)).includes('assign')) || datasets[0];
  const actualDs = datasets.find(d => norm(String(d.label)).includes('confirm')) || datasets[1];
  if (!budgetDs?.data || !actualDs?.data) return [];
  const rows = labels.map((label, i) => {
    const assigned = Number(budgetDs.data[i]) || 0;
    const actual = Number(actualDs.data[i]) || 0;
    const rawEff = assigned > 0 ? (actual / assigned) * 100 : actual > 0 ? 100 : 0;
    return { label: String(label), assigned, actual, rawEff };
  });
  return rows.sort((a, b) => b.actual - a.actual);
}

function computeEfficiencyStrip(rows) {
  const scored = rows
    .map(r => {
      if (r.assigned > 0) return { label: r.label, eff: (r.actual / r.assigned) * 100 };
      if (r.actual > 0) return { label: r.label, eff: 100 };
      return null;
    })
    .filter(Boolean);

  if (!scored.length) return { best: '—', worst: '—', avg: '—' };

  let best = scored[0];
  let worst = scored[0];
  let sum = 0;
  scored.forEach(s => {
    sum += s.eff;
    if (s.eff > best.eff) best = s;
    if (s.eff < worst.eff) worst = s;
  });
  const avg = sum / scored.length;
  return { best: best.label, worst: worst.label, avg: `${Math.round(avg * 10) / 10}%` };
}

function buildOption(rows) {
  if (!rows.length) return {};

  const reversed = [...rows].reverse();
  const categories = reversed.map(r => r.label);
  const assignedData = reversed.map(r => r.assigned);
  const actualData = reversed.map(r => r.actual);
  const effData = reversed.map(r => r.rawEff);

  return {
    ...defaultAnimation,
    grid: { ...horizontalGrid, right: 70 },
    tooltip: {
      ...premiumTooltip,
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148,163,184,0.06)' } },
      formatter(params) {
        if (!Array.isArray(params) || !params.length) return '';
        const idx = params[0].dataIndex;
        const source = categories[idx];
        const assigned = assignedData[idx];
        const actual = actualData[idx];
        const eff = effData[idx];
        const pctStr = `${Math.round(eff * 10) / 10}%`;
        const clr = effColor(eff);
        return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${source}</div>`
          + `<div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:2px">`
          + `<span style="color:#94a3b8">Assigned</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(assigned)}</span></div>`
          + `<div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:2px">`
          + `<span style="color:#94a3b8">Actualized</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(actual)}</span></div>`
          + `<div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px;padding-top:4px;border-top:1px solid rgba(148,163,184,0.2)">`
          + `<span style="color:#94a3b8">Efficiency</span><span style="font-weight:700;color:${clr}">${pctStr}</span></div>`;
      },
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        ...axisLabelStyle,
        formatter: v => formatIndianAuto(v),
      },
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        ...axisLabelStyle,
        width: 110,
        overflow: 'truncate',
      },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Assigned',
        type: 'bar',
        data: assignedData,
        barWidth: '50%',
        barGap: '-100%',
        z: 1,
        itemStyle: {
          color: '#E5E7EB',
          borderRadius: [0, 4, 4, 0],
        },
        emphasis: { disabled: true },
        silent: true,
      },
      {
        name: 'Actualized',
        type: 'bar',
        data: actualData,
        barWidth: '50%',
        z: 2,
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
          color: (params) => effColor(effData[params.dataIndex]),
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          fontWeight: 600,
          color: '#6B7280',
          formatter: (params) => {
            const eff = effData[params.dataIndex];
            return `${Math.round(eff)}%`;
          },
        },
      },
    ],
  };
}

const SourceActualizationProgress = ({
  data,
  name,
  handleExport,
  handleEnlarge,
  showButton = true,
  embeddedInDialog = false,
}) => {
  const currencyContext = useSelector(currencySelector);
  const currencyKey = `${currencyContext?.currencySymbol || ''}|${currencyContext?.currency || ''}|${currencyContext?.currencyLocale || ''}`;
  const displayName = data?.name || name;
  const { rows, effStrip } = useMemo(() => {
    const r = buildRows(data);
    return { rows: r, effStrip: computeEfficiencyStrip(r) };
  }, [data]);

  const option = useMemo(() => buildOption(rows), [rows]);

  const plotHeight = useMemo(() => {
    const n = rows.length || 1;
    if (embeddedInDialog) {
      return Math.min(720, Math.max(320, n * 42));
    }
    return Math.min(400, Math.max(200, n * 36));
  }, [rows.length, embeddedInDialog]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const insightText = data?.infoText || 'Actual vs assigned — green over 90%, amber 60–90%, red under 60%';

  const paperSx = embeddedInDialog
    ? {
        ...reportChartPaperSx,
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: 'none',
        px: 0,
        py: 0,
      }
    : reportChartPaperSx;

  return (
    <Paper sx={paperSx}>
      {!embeddedInDialog && (
        <ReportCardHeader
          title={displayName}
          insight={insightText}
          handleExport={handleExport}
          handleEnlarge={handleEnlarge}
          showButton={showButton}
        />
      )}

      <ReportKpiStrip
        columns={3}
        items={apiKpis || [
          { label: 'Best efficiency', value: effStrip.best },
          { label: 'Lowest efficiency', value: effStrip.worst },
          { label: 'Avg efficiency', value: effStrip.avg },
        ]}
      />

      <Box
        sx={
          embeddedInDialog
            ? { width: '100%', flex: '1 1 auto', minHeight: plotHeight, minWidth: 0 }
            : { width: '100%', height: plotHeight }
        }>
        <ReactECharts key={currencyKey} option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
      </Box>
    </Paper>
  );
};

export default SourceActualizationProgress;
